import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { changePassword } from '../../services/auth.service';
import { notifyError, notifySuccess } from '../../lib/toast';
import { Field } from '../../components/shared/Field';
import { Input } from '../../components/shared/Input';
import { Button } from '../../components/shared/Button';
import { SettingsCard, ReadOnlyRow } from './SettingsCard';

// Mirrors changePasswordSchema and adds a confirm field, the same way ResetPasswordPage mirrors
// resetPasswordSchema. The confirm box is form-only — the API has no use for it, and a typo'd new
// password is precisely the mistake this screen must not let through, since the old one stops
// working the moment it saves.
const passwordFormSchema = z
  .object({
    current_password: z.string().min(1, 'Enter your current password'),
    new_password: z.string().min(8, 'At least 8 characters').max(72),
    confirm: z.string(),
  })
  .refine((d) => d.new_password === d.confirm, {
    path: ['confirm'],
    message: 'Passwords do not match',
  })
  .refine((d) => d.current_password !== d.new_password, {
    path: ['new_password'],
    message: 'Choose a password different from your current one',
  });

type PasswordFormValues = z.infer<typeof passwordFormSchema>;

interface SecuritySettingsProps {
  email: string | null;
}

export function SecuritySettings({ email }: SecuritySettingsProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: { current_password: '', new_password: '', confirm: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await changePassword({
        current_password: values.current_password,
        new_password: values.new_password,
      });
      // Clear the form on success — leaving three filled password boxes on screen after a
      // successful change is both untidy and a small shoulder-surfing risk.
      reset();
      notifySuccess('Password changed — your other devices have been signed out');
    } catch (err) {
      // A wrong current password comes back as 403 INVALID_CURRENT_PASSWORD and surfaces here as
      // the action's outcome, like every other failed submit.
      notifyError(err, 'Could not change your password');
    }
  });

  return (
    <>
      <SettingsCard
        title="Change password"
        description="You'll stay signed in here. Every other device you're signed in on will be signed out."
      >
        <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
          {/* A hidden username field so password managers can associate the new password with the
              right account. Without it they routinely offer to save it under the wrong entry, or
              refuse to update the existing one. */}
          <input
            type="text"
            name="username"
            autoComplete="username"
            value={email ?? ''}
            readOnly
            hidden
          />
          <Field
            label="Current password"
            htmlFor="current_password"
            error={errors.current_password?.message}
          >
            <Input
              id="current_password"
              type="password"
              autoComplete="current-password"
              {...register('current_password')}
            />
          </Field>
          <Field label="New password" htmlFor="new_password" error={errors.new_password?.message}>
            <Input
              id="new_password"
              type="password"
              autoComplete="new-password"
              {...register('new_password')}
            />
          </Field>
          <Field label="Confirm new password" htmlFor="confirm" error={errors.confirm?.message}>
            <Input id="confirm" type="password" autoComplete="new-password" {...register('confirm')} />
          </Field>
          <div>
            <Button type="submit" variant="accent" size="sm" disabled={isSubmitting}>
              {isSubmitting ? 'Changing…' : 'Change password'}
            </Button>
          </div>
        </form>
      </SettingsCard>

      <SettingsCard title="Sign-in">
        <ReadOnlyRow
          label="Email"
          value={email ?? '—'}
          note="Ask your federation admin if this needs to change."
        />
        <ReadOnlyRow
          label="Two-factor authentication"
          value="Not available yet"
          note="Planned, and will be required for admin accounts."
        />
      </SettingsCard>
    </>
  );
}
